import { io } from 'socket.io-client'
import { ElNotification } from 'element-plus'

class SocketService {
    constructor() {
        this.socket = null
        this.connected = false
        this.listeners = new Map()
    }

    // 连接到WebSocket服务器
    connect(token) {
        if (this.socket?.connected) {
            console.log('WebSocket已经连接')
            return this.socket
        }

        this.socket = io(import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000', {
            transports: ['websocket', 'polling'],
            auth: {
                token
            }
        })

        this.socket.on('connect', () => {
            console.log('✅ WebSocket连接成功:', this.socket.id)
            this.connected = true
            ElNotification({
                title: '连接成功',
                message: '实时通信已建立',
                type: 'success',
                duration: 2000
            })
        })

        this.socket.on('disconnect', (reason) => {
            console.log('❌ WebSocket断开连接:', reason)
            this.connected = false
            ElNotification({
                title: '连接断开',
                message: '实时通信已断开',
                type: 'warning',
                duration: 2000
            })
        })

        this.socket.on('error', (error) => {
            console.error('WebSocket错误:', error)
            ElNotification({
                title: '连接错误',
                message: error.message || '实时通信出现错误',
                type: 'error',
                duration: 3000
            })
        })

        // 接收消息
        this.socket.on('receive_message', (data) => {
            console.log('📨 收到消息:', data)
            this.emit('message', data)
        })

        // 设备状态更新
        this.socket.on('device_status_update', (data) => {
            console.log('📡 设备状态更新:', data)
            this.emit('device_status', data)
        })

        // GPS位置更新
        this.socket.on('gps_position_update', (data) => {
            console.log('📍 GPS位置更新:', data)
            this.emit('gps_position', data)
        })

        // 北斗短报文
        this.socket.on('beidou_message', (data) => {
            console.log('🛰️ 北斗短报文:', data)
            this.emit('beidou_message', data)
            ElNotification({
                title: '新的北斗短报文',
                message: `来自: ${data.sender}`,
                type: 'info',
                duration: 3000
            })
        })

        // 天通短信
        this.socket.on('ttcat_sms', (data) => {
            console.log('📱 天通短信:', data)
            this.emit('ttcat_sms', data)
            ElNotification({
                title: '新的天通短信',
                message: `来自: ${data.sender}`,
                type: 'info',
                duration: 3000
            })
        })

        // 系统告警
        this.socket.on('system_alert', (data) => {
            console.log('⚠️ 系统告警:', data)
            this.emit('system_alert', data)
            ElNotification({
                title: '系统告警',
                message: data.message,
                type: data.level === 'error' ? 'error' : 'warning',
                duration: 5000
            })
        })

        return this.socket
    }

    // 断开连接
    disconnect() {
        if (this.socket) {
            this.socket.disconnect()
            this.socket = null
            this.connected = false
            this.listeners.clear()
            console.log('WebSocket已断开')
        }
    }

    // 加入房间
    joinRoom(roomId) {
        if (this.socket?.connected) {
            this.socket.emit('join_room', roomId)
            console.log(`加入房间: ${roomId}`)
        }
    }

    // 发送消息
    sendMessage(roomId, message, senderId) {
        if (this.socket?.connected) {
            this.socket.emit('send_message', {
                roomId,
                message,
                senderId
            })
        } else {
            console.error('WebSocket未连接')
        }
    }

    // 发送GPS位置
    sendGPSPosition(data) {
        if (this.socket?.connected) {
            this.socket.emit('gps_position', data)
        }
    }

    // 发送设备状态
    sendDeviceStatus(data) {
        if (this.socket?.connected) {
            this.socket.emit('device_status', data)
        }
    }

    // 注册事件监听器
    on(event, callback) {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, [])
        }
        this.listeners.get(event).push(callback)
    }

    // 移除事件监听器
    off(event, callback) {
        if (this.listeners.has(event)) {
            const callbacks = this.listeners.get(event)
            const index = callbacks.indexOf(callback)
            if (index > -1) {
                callbacks.splice(index, 1)
            }
        }
    }

    // 触发事件
    emit(event, data) {
        if (this.listeners.has(event)) {
            this.listeners.get(event).forEach(callback => {
                try {
                    callback(data)
                } catch (error) {
                    console.error(`事件处理错误 [${event}]:`, error)
                }
            })
        }
    }

    // 检查连接状态
    isConnected() {
        return this.connected && this.socket?.connected
    }
}

// 创建单例
const socketService = new SocketService()

export default socketService